export const PRODUCT_CATEGORIES = [
  { id: 'commercial', label: 'Commercial Lines', desc: 'Coverage built for businesses — from trucking fleets to storefronts, offices, and everything in between.' },
  { id: 'personal', label: 'Personal Lines', desc: 'Protection for the people, property, and lifestyle you have worked hard to build.' },
];

export const PRODUCTS = [
  /* ═════════ COMMERCIAL LINES ═════════ */
  {
    slug: 'business-owners',
    category: 'commercial',
    title: 'Business Owners',
    shortTitle: 'Business',
    icon: '🏢',
    iconName: 'Building2',
    bestFor: 'Small to mid-sized businesses',
    heroColor: '#0a1628',
    image: '/images/products/business-owners.jpg',
    tagline: 'Comprehensive protection for your business operations.',
    description: 'A Business Owners Policy (BOP) bundles essential coverages into one convenient, cost-effective policy. Protect your property, operations, and income with tailored coverage for small to mid-sized businesses.',
    coverages: [
      'Commercial Property Coverage',
      'General Liability Protection',
      'Business Interruption Insurance',
      'Equipment Breakdown Coverage',
      'Cyber Liability Options',
      'Employee Dishonesty Coverage',
    ],
    features: [
      { icon: '🛡️', label: 'All-in-One Policy', desc: 'Combine multiple coverages for savings' },
      { icon: '⚡', label: 'Fast Quotes', desc: 'Get coverage within 24 hours' },
      { icon: '🤝', label: 'Dedicated Agent', desc: 'Personal support whenever you need' },
    ],
    keywords: ['BOP', 'business insurance', 'commercial', 'property', 'liability', 'small business'],
  },
  {
    slug: 'transportation',
    category: 'commercial',
    title: 'Transportation',
    shortTitle: 'Transportation',
    icon: '🚛',
    iconName: 'Truck',
    bestFor: 'Owner-operators & fleets',
    heroColor: '#0f2142',
    image: '/images/products/transportation.jpg',
    tagline: 'Specialized coverage for trucking and transport operations.',
    description: 'From owner-operators to fleets, we provide comprehensive transportation insurance. Commercial auto, cargo, physical damage, and more — protecting your vehicles and livelihood on every mile.',
    coverages: [
      'Commercial Auto Liability ($1M CSL)',
      'Motor Truck Cargo Insurance',
      'Physical Damage (Collision + Comprehensive)',
      'Reefer Breakdown Coverage',
      'Trailer Interchange',
      'Non-Owned Trailer Coverage',
      'Bobtail / Non-Trucking Liability',
    ],
    features: [
      { icon: '📋', label: 'DOT Compliance', desc: 'Meet all federal filing requirements' },
      { icon: '🔄', label: 'Flexible Payments', desc: 'Monthly payment plans available' },
      { icon: '📞', label: '24/7 Claims', desc: 'Support when it matters most' },
    ],
    keywords: ['trucking', 'transportation', 'cargo', 'commercial auto', 'DOT', 'FMCSA', 'reefer', 'owner operator'],
  },
  {
    slug: 'commercial-property',
    category: 'commercial',
    title: 'Commercial Property',
    shortTitle: 'Commercial Property',
    icon: '🏗️',
    iconName: 'Building',
    bestFor: 'Property & asset owners',
    heroColor: '#162d54',
    image: '/images/products/commercial-property.jpg',
    tagline: 'Guard your real estate and physical assets.',
    description: 'Protect your buildings, equipment, inventory, and more with commercial property insurance customized to your industry and risk profile.',
    coverages: [
      'Building & Structure Coverage',
      'Business Personal Property',
      'Inventory & Stock Protection',
      'Equipment & Machinery',
      'Loss of Income / Business Interruption',
      'Natural Disaster Add-ons',
    ],
    features: [
      { icon: '🏠', label: 'Property Value', desc: 'Replacement cost coverage available' },
      { icon: '🌪️', label: 'All-Risk Coverage', desc: 'Broad form named perils' },
      { icon: '💼', label: 'Industry Specific', desc: 'Tailored to your sector' },
    ],
    keywords: ['commercial property', 'building', 'real estate', 'inventory', 'equipment'],
  },
  {
    slug: 'workers-compensation',
    category: 'commercial',
    title: "Workers' Compensation",
    shortTitle: "Workers' Comp",
    icon: '👷',
    iconName: 'HardHat',
    bestFor: 'Employers with staff',
    heroColor: '#0a1628',
    image: '/images/products/workers-compensation.jpg',
    tagline: 'Protect your employees and your business.',
    description: "Workers' compensation insurance covers medical expenses and lost wages for employees injured on the job — keeping your team protected and your business compliant.",
    coverages: [
      'Medical Expense Coverage',
      'Lost Wage Replacement',
      'Disability Benefits',
      'Death Benefits',
      "Employer's Liability",
      'Return-to-Work Programs',
    ],
    features: [
      { icon: '⚖️', label: 'State Compliant', desc: 'Meet all state requirements' },
      { icon: '🏥', label: 'Medical Coverage', desc: 'Full medical expense protection' },
      { icon: '📊', label: 'Payroll Based', desc: 'Premiums scale with your workforce' },
    ],
    keywords: ["workers comp", "workers compensation", "employee injury", "workplace"],
  },
  {
    slug: 'professional-liability',
    category: 'commercial',
    title: 'Professional Liability / E&O',
    shortTitle: 'Professional Liability',
    icon: '⚖️',
    iconName: 'Scale',
    bestFor: 'Consultants & service pros',
    heroColor: '#0f2142',
    image: '/images/products/professional-liability.jpg',
    tagline: "Cover your expertise against claims of negligence.",
    description: 'Errors & Omissions (E&O) insurance protects professionals from claims alleging negligent acts, errors, or omissions in the services you provide.',
    coverages: [
      'Negligence & Errors Claims',
      'Omissions in Professional Services',
      'Defense Costs Coverage',
      'Settlements & Judgments',
      'Claims-Made Policy Form',
      'Prior Acts Coverage Available',
    ],
    features: [
      { icon: '🎓', label: 'Industry Expertise', desc: 'Specialized by profession' },
      { icon: '⚡', label: 'Legal Defense', desc: 'Full defense cost coverage' },
      { icon: '🛡️', label: 'Retroactive Cover', desc: 'Prior acts protection available' },
    ],
    keywords: ['E&O', 'professional liability', 'errors omissions', 'consultant', 'negligence'],
  },
  {
    slug: 'epli',
    category: 'commercial',
    title: 'Employment Practices Liability (EPLI)',
    shortTitle: 'EPLI',
    icon: '👥',
    iconName: 'Users',
    bestFor: 'Companies managing teams',
    heroColor: '#162d54',
    image: '/images/products/epli.jpg',
    tagline: 'Shield your business from employment-related claims.',
    description: 'EPLI covers your business against claims by employees alleging discrimination, wrongful termination, harassment, and other employment-related issues.',
    coverages: [
      'Wrongful Termination Claims',
      'Discrimination Allegations',
      'Sexual Harassment Claims',
      'Retaliation Claims',
      'Failure to Promote',
      'Wage & Hour Disputes',
    ],
    features: [
      { icon: '📋', label: 'HR Resources', desc: 'Included risk management tools' },
      { icon: '💬', label: 'Employment Hotline', desc: 'Expert HR guidance' },
      { icon: '🛡️', label: 'Defense & Settlement', desc: 'Both covered under policy' },
    ],
    keywords: ['EPLI', 'employment practices', 'discrimination', 'harassment', 'wrongful termination'],
  },
  {
    slug: 'umbrella',
    category: 'commercial',
    title: 'Commercial Umbrella',
    shortTitle: 'Umbrella',
    icon: '☂️',
    iconName: 'Umbrella',
    bestFor: 'High-exposure businesses',
    heroColor: '#0a1628',
    image: '/images/products/umbrella.jpg',
    tagline: 'An extra layer of liability protection above your business policies.',
    description: "Commercial Umbrella Insurance provides an additional layer of liability protection above the limits of your underlying business insurance policies. In today's litigious environment, a single large lawsuit can exceed the limits of a General Liability, Commercial Auto, or Employers Liability policy. Umbrella coverage helps protect your business, assets, and future earnings from catastrophic claims.",
    coverages: [
      'Excess General Liability',
      'Excess Commercial Auto Liability',
      'Excess Employers Liability',
      'Bodily Injury Claims',
      'Property Damage Claims',
      'Personal and Advertising Injury Claims',
      'Certain Legal Defense Costs',
      'Catastrophic Lawsuits and Judgments',
    ],
    keyFeatures: [
      'Coverage limits starting at $1 million and often extending much higher',
      'Additional protection above underlying policy limits',
      'Broad liability protection for growing businesses',
      'Available for a wide range of industries',
      "Helps address today's increasing lawsuit settlements and verdicts",
      'Can be customized based on business size and exposure',
    ],
    whyChoose: [
      'Provides protection when primary policy limits are exhausted',
      'Helps safeguard business assets and future revenue',
      'Protects against large lawsuits and unexpected claims',
      'Offers peace of mind for business owners and executives',
      'Often one of the most cost-effective ways to increase liability protection',
    ],
    keywords: ['umbrella', 'excess liability', 'commercial umbrella', 'additional coverage', 'high limits'],
  },
  {
    slug: 'commercial-earthquake',
    category: 'commercial',
    title: 'Commercial Earthquake',
    shortTitle: 'Earthquake',
    icon: '🌋',
    iconName: 'AlertTriangle',
    bestFor: 'California business & property owners',
    heroColor: '#0f2142',
    image: '/images/products/commercial-earthquake.jpg',
    tagline: 'Protect your business from seismic risk.',
    description: 'Commercial Earthquake Insurance helps protect businesses and commercial property owners from the financial impact of earthquake-related damage. In California, standard commercial property policies typically exclude earthquake losses, making specialized coverage an important part of a comprehensive risk management strategy.',
    coverages: [
      'Building Damage',
      'Tenant Improvements & Betterments',
      'Business Personal Property',
      'Equipment and Machinery',
      'Inventory and Stock',
      'Business Income Loss',
      'Extra Expense Coverage',
      'Debris Removal',
      'Certain Building Code Upgrade Costs',
    ],
    keyFeatures: [
      'Coverage for commercial buildings and investment properties',
      'Protection for owner-occupied and tenant-occupied properties',
      'Business interruption coverage options',
      'Flexible deductible structures',
      'Coverage for a wide range of industries and property types',
      'Stand-alone and layered earthquake solutions available',
    ],
    whyChoose: [
      'Standard commercial property policies generally exclude earthquake damage',
      'Helps protect against catastrophic financial losses',
      'Preserves business continuity following a major event',
      'Protects property values, cash flow, and long-term investments',
      'Essential coverage for California commercial property owners',
    ],
    keywords: ['earthquake', 'commercial earthquake', 'seismic', 'california', 'business interruption'],
  },
  {
    slug: 'surety-bonds',
    category: 'commercial',
    title: 'Surety Bonds',
    shortTitle: 'Surety Bonds',
    icon: '📜',
    iconName: 'FileCheck',
    bestFor: 'Contractors & licensed businesses',
    heroColor: '#162d54',
    image: '/images/products/surety-bonds.jpg',
    tagline: 'Bonds that keep your business compliant and credible.',
    description: 'A Surety Bond is a financial guarantee that helps ensure contractual obligations, legal requirements, or licensing regulations are fulfilled. Unlike insurance, a bond protects the party requiring the bond, while providing assurance that the bonded individual or business will perform as agreed.',
    coverages: [
      'Contractor License Requirements',
      'Bid Bonds',
      'Performance Bonds',
      'Payment Bonds',
      'Permit Bonds',
      'Commercial Bonds',
      'Court Bonds',
      'Fidelity and Employee Dishonesty Bonds',
      'Notary Bonds',
      'Public Official Bonds',
    ],
    keyFeatures: [
      'Fast bond issuance for many bond types',
      'Access to multiple surety markets',
      'Competitive rates',
      'Bonds available for businesses of all sizes',
      'Support for new and established contractors',
      'Electronic bond delivery available for many bond types',
    ],
    whyChoose: [
      'Satisfies state, municipal, and contractual requirements',
      'Demonstrates financial credibility and professionalism',
      'Helps businesses qualify for projects and contracts',
      'Supports growth opportunities and larger contract awards',
      'Expert guidance through the bonding process',
    ],
    keywords: ['surety bond', 'license bond', 'permit bond', 'contract bond', 'performance bond', 'bid bond', 'notary bond'],
  },

  /* ═════════ PERSONAL LINES ═════════ */
  {
    slug: 'life-insurance',
    category: 'personal',
    title: 'Life Insurance',
    shortTitle: 'Life Insurance',
    icon: '❤️',
    iconName: 'HeartPulse',
    bestFor: 'Families & income earners',
    heroColor: '#0a1628',
    image: '/images/products/life-insurance.jpg',
    tagline: "Secure your family's financial future.",
    description: "Life insurance helps protect your loved ones financially if something happens to you. It can provide income replacement, pay off debts, cover final expenses, and help secure your family's future.",
    coverages: [
      'Death Benefit for Beneficiaries',
      'Income Replacement',
      'Mortgage Payoff',
      'Final Expenses and Funeral Costs',
      'Business Succession Planning',
      'Estate Planning Needs',
      "Children's Education Funding",
    ],
    keyFeatures: [
      'Term Life Insurance',
      'Whole Life Insurance',
      'Universal Life Insurance',
      'Indexed Universal Life (IUL)',
      'Business Life Insurance',
      'Key Person Coverage',
    ],
    whyChoose: [
      'Protects family financial security',
      'Tax-free death benefits in most cases',
      'Flexible coverage options',
      'Helps preserve wealth for future generations',
    ],
    keywords: ['life insurance', 'term life', 'whole life', 'IUL', 'key person', 'family', 'final expense'],
  },
  {
    slug: 'classics-exotics',
    category: 'personal',
    title: 'Classic & Exotic Cars',
    shortTitle: 'Classics & Exotics',
    icon: '🏎️',
    iconName: 'Car',
    bestFor: 'Collectors & enthusiasts',
    heroColor: '#0f2142',
    image: '/images/products/classics-exotics.jpg',
    tagline: 'Specialized coverage for collector and exotic vehicles.',
    description: 'Standard auto insurance often falls short when it comes to collector, classic, luxury, and exotic vehicles. Specialized coverage helps protect the true value of your investment.',
    coverages: [
      'Agreed Value Coverage',
      'Collision and Comprehensive',
      'Theft Protection',
      'Fire Damage',
      'Transport Coverage',
      'Spare Parts Coverage',
      'Liability Protection',
    ],
    keyFeatures: [
      'Agreed value settlements',
      'Flexible mileage options',
      'Coverage for collections',
      'Specialized repair networks',
      'Coverage for exotic and high-performance vehicles',
    ],
    whyChoose: [
      "Protects the vehicle's collectible value",
      'No depreciation concerns with agreed value policies',
      'Designed specifically for rare and specialty vehicles',
      'Coverage tailored to enthusiasts and collectors',
    ],
    keywords: ['classic car', 'exotic car', 'collector', 'agreed value', 'high value auto', 'vintage', 'luxury'],
  },
  {
    slug: 'homeowners',
    category: 'personal',
    title: 'Homeowners',
    shortTitle: 'Homeowners',
    icon: '🏡',
    iconName: 'Home',
    bestFor: 'Homeowners & landlords',
    heroColor: '#162d54',
    image: '/images/products/homeowners.jpg',
    tagline: 'Protect the place your life happens.',
    description: 'Your home is likely your largest investment. Homeowners insurance helps protect your property, belongings, and financial future from unexpected losses.',
    coverages: [
      'Dwelling Coverage',
      'Other Structures',
      'Personal Property',
      'Loss of Use',
      'Personal Liability',
      'Medical Payments to Others',
    ],
    keyFeatures: [
      'Replacement Cost Coverage',
      'Personal Property Protection',
      'Liability Coverage',
      'Additional Living Expenses',
      'Optional Flood and Earthquake Solutions',
    ],
    whyChoose: [
      'Protects your home and assets',
      'Coverage customized to your property',
      "Guidance through California's changing insurance market",
      'Access to FAIR Plan and DIC solutions when needed',
    ],
    keywords: ['homeowners', 'home insurance', 'fire zone', 'coastal', 'condo', 'landlord', 'rental property', 'FAIR plan'],
  },
  {
    slug: 'boats-watercraft',
    category: 'personal',
    title: 'Boats & Watercraft',
    shortTitle: 'Boats & Watercraft',
    icon: '🛥️',
    iconName: 'Anchor',
    bestFor: 'Boat & watercraft owners',
    heroColor: '#0a1628',
    image: '/images/products/boats-watercraft.jpg',
    tagline: 'Coverage for life on the water.',
    description: "Whether you're enjoying weekends on the lake or spending time on the open water, specialized watercraft insurance helps protect your vessel and your liability exposure.",
    coverages: [
      'Physical Damage',
      'Collision',
      'Theft',
      'Liability',
      'Medical Payments',
      'Uninsured Boater Coverage',
      'Towing and Assistance',
    ],
    keyFeatures: [
      'Coverage for boats, yachts, jet skis, and personal watercraft',
      'Navigation territory options',
      'Emergency assistance',
      'Fuel spill liability coverage',
      'Fishing equipment coverage options',
    ],
    whyChoose: [
      'Protection on and off the water',
      'Coverage tailored to your vessel',
      'Flexible options for recreational and luxury watercraft',
    ],
    keywords: ['boat insurance', 'watercraft', 'jet ski', 'yacht', 'personal watercraft', 'recreational'],
  },
  {
    slug: 'personal-earthquake',
    category: 'personal',
    title: 'Earthquake',
    shortTitle: 'Earthquake',
    icon: '🌎',
    iconName: 'Activity',
    bestFor: 'California homeowners & renters',
    heroColor: '#0f2142',
    image: '/images/products/personal-earthquake.jpg',
    tagline: "Your homeowners policy doesn't cover earthquakes. We do.",
    description: "Most homeowners policies do not cover earthquake damage. Earthquake insurance helps protect your home, property, and financial future against one of California's most significant risks.",
    coverages: [
      'Dwelling Damage',
      'Personal Property',
      'Building Code Upgrades',
      'Additional Living Expenses',
      'Foundation and Structural Damage',
    ],
    keyFeatures: [
      'Residential and Commercial Solutions',
      'Flexible Deductible Options',
      'Coverage for Older and Newer Structures',
      'Protection Against Catastrophic Losses',
    ],
    whyChoose: [
      'Earthquake coverage is not included in standard homeowners policies',
      'Helps protect against devastating financial losses',
      'Specialized solutions for California property owners',
    ],
    keywords: ['earthquake insurance', 'seismic', 'california earthquake', 'home earthquake'],
  },
  {
    slug: 'personal-umbrella',
    category: 'personal',
    title: 'Umbrella',
    shortTitle: 'Umbrella',
    icon: '☂️',
    iconName: 'Umbrella',
    bestFor: 'Protecting personal assets',
    heroColor: '#162d54',
    image: '/images/products/personal-umbrella.jpg',
    tagline: 'Extra liability protection above your personal policies.',
    description: 'Umbrella insurance provides an extra layer of liability protection above your existing home, auto, boat, and other personal insurance policies.',
    coverages: [
      'Excess Liability',
      'Personal Injury Claims',
      'Bodily Injury Claims',
      'Property Damage Claims',
      'Legal Defense Costs',
    ],
    keyFeatures: [
      'Coverage limits from $1 million and higher',
      'Broad liability protection',
      'Worldwide coverage in many cases',
      'Affordable additional protection',
    ],
    whyChoose: [
      'Protects savings, investments, and future income',
      'Provides peace of mind against large lawsuits',
      'Extends protection beyond standard policy limits',
      'Essential for high-net-worth individuals and families',
    ],
    keywords: ['personal umbrella', 'excess liability', 'asset protection', 'liability'],
  },
];

export const REVIEWS = [
  {
    name: 'Lori Elmquist',
    role: 'HR / Controller',
    business: 'J Med Supplies, Inc.',
    logo: '/images/reviews/jmed.png',
    avatar: 'L',
    rating: 5,
    date: 'Google Review',
    link: 'https://www.google.com/maps/contrib/113097009801543136550/reviews?hl=en-GB',
    text: "Prime Sentinel Insurance Solution has been a God send. We've been doing business with Chrishan for over 4 years. I was so happy to hear he was going into business for himself. He has so many other options for insurance needs that his previous company couldn't help with. We were opening new locations and moving others — since the location was under Medicare Guidelines it got a little sticky, but he was able to get us coverage within 48 hours. He's helped us with Auto, EPLI, GL, Surety Bonds, and much more. Thank you for jumping through hoops for J Med Supplies, Inc.",
  },
  {
    name: 'Mustafa Sadozai',
    role: 'Owner / CEO',
    business: 'Joyland Coin Laundry & Canoga Laundry',
    logo: '',
    avatar: 'M',
    rating: 5,
    date: 'Google Review',
    link: 'https://share.google/04F3rPpnKLoCoOuMn',
    text: "We operate three laundromats, and working with Chrishan has been an outstanding experience from start to finish. He took the time to truly understand our business, explain our coverage options clearly, and make sure each location was properly protected. The insurance packages he put together for us offer excellent coverage and great value, and his attention to detail has given us real peace of mind. What really stands out is his service. Chrishan is always responsive, professional, and willing to go the extra mile — whether it's answering questions, making changes, or helping us navigate renewals and claims. It's rare to find someone so knowledgeable and genuinely committed to their clients. We are extremely grateful for his support and highly recommend Chrishan to any business owner looking for a trustworthy and reliable insurance broker.",
  },
  {
    name: 'Shaheen Ghazaly',
    role: 'Owner / Chef',
    business: 'Kurry Pinch',
    logo: '/images/reviews/kurrypinch.png',
    avatar: 'S',
    rating: 5,
    date: 'Google Review',
    link: 'https://maps.app.goo.gl/LqCTjENPDQrsGy348',
    text: "I've had a great experience with Prime Sentinel Insurance Solutions for my business insurance and workers' compensation coverage. Their service is efficient, responsive, and very professional. Chrishan has been outstanding — always quick to respond, patient in explaining details, and proactive in making sure my business is properly covered. It gives me peace of mind knowing I can consult him anytime I need assistance or clarification. If you're looking for reliable and knowledgeable insurance support for your business, Prime Sentinel Insurance Solutions is a great choice.",
  },
  {
    name: 'Prabhjot Sekhon',
    role: 'CEO / Owner',
    business: 'Eakprab LLC & Pet Supplies Plus',
    logo: '/images/reviews/petsupplies.png',
    avatar: 'P',
    rating: 5,
    date: 'Google Review',
    link: 'https://share.google/jIzryaTM2l6zjjEEK',
    text: "I have never had a more straightforward experience with getting insurance. They found us a fantastic deal on my storage unit property with more coverage and for a better price than what I was paying initially. They are very responsive and answer all your questions earnestly and with keeping you in mind. My pet store was the same — they helped me find a better quote for our insurance and got us better coverage for less. I cannot recommend Chrishan and Amandeep enough for insurance needs!",
  },
  {
    name: 'Hiran Wirekoon',
    role: 'CEO',
    business: 'Simi Valley Montessori',
    logo: '/images/reviews/montessori.png',
    avatar: 'H',
    rating: 5,
    date: 'Google Review',
    link: 'https://share.google/wtKncucnR7ughIouT',
    text: "I had a great experience working with Chrishan. From the very beginning, he took the time to clearly walk us through the entire process and made sure we understood our options. Commercial insurance can often feel complicated, but his ability to explain things in a straightforward and thoughtful way made it much easier for us to make informed decisions. He was consistently responsive, prompt, and professional. What stood out most was his willingness to explore different options to ensure the coverage aligned with our business needs. He approached the relationship as a true partner rather than just a broker. I would highly recommend him to any business looking for a knowledgeable, responsive, and trustworthy commercial insurance advisor.",
  },
  {
    name: 'Justin Estorga',
    role: 'CEO',
    business: 'Under The Sea Inc.',
    logo: '/images/reviews/underthesea.png',
    avatar: 'J',
    rating: 5,
    date: 'Google Review',
    link: 'https://share.google/2cPXQPMBQmt6rsBrV',
    text: "As an owner of Under the Sea Inc., protecting our landscaping business with the right general liability insurance is non-negotiable. Working with Chrishan at Prime Sentinel Insurance was outstanding. He was professional, prompt, and extremely knowledgeable about insurance coverage for landscaping contractors. He made the entire process seamless, transparent, and efficient — clearly explaining our policy and ensuring we understood exactly how our business is protected. What sets him apart is his integrity. No pressure, no confusion — just honest guidance and reliable service. If you're a landscaping contractor or small business owner looking for dependable general liability insurance, contact Chrishan at Prime Sentinel Insurance.",
  },
];

export const TEAM = [
  {
    name: 'Chrishan De Silva',
    role: 'Partner / Agent',
    initials: 'CD',
    image: '/images/team/chrishan.jpg',
    phone: '818-600-0821',
    email: 'chrishan@primesentinelins.com',
    bio: "With over a decade of experience in the insurance industry, I've seen firsthand how confusing and overwhelming insurance can be for many people. After years of working within large corporations, I started Prime Sentinel because I believed businesses and families deserved more — more clarity, more options, and more genuine support. Too often, people were left with limited choices, poor communication, and a lack of guidance. I set out to change that. At Prime Sentinel, we're not just selling policies — we're building trusted partnerships. My mission is to break down the complexities of insurance, provide tailored solutions, and help clients protect what matters with confidence and peace of mind. Outside of work, I enjoy reading, traveling, and spending quality time with my family and close friends. Helping people is my passion — and I bring that same energy and purpose into everything I do at Prime Sentinel.",
  },
  {
    name: 'Amandeep Samra',
    role: 'Partner / Agent',
    initials: 'AS',
    image: '/images/team/amandeep.jpg',
    phone: '818-290-6946',
    email: 'Amandeep@primesentinelins.com',
    bio: "With over 7 years of experience at Farmers Insurance, I've developed a deep understanding of how to truly protect people, businesses, and livelihoods. Over the years, I saw firsthand how the insurance landscape was shifting — becoming more complex, less personal, and harder for clients to navigate. That realization is what sparked the creation of Prime Sentinel Insurance. At Prime Sentinel, we specialize in business and trucking insurance, offering customized solutions for everything from small local operations to large fleets. Whether it's protecting your business assets or ensuring your trucks stay on the road and compliant, I take pride in being a reliable partner who's always in your corner. My mission is to make insurance not just easier but smarter, more transparent, and genuinely helpful for every client I work with. When I'm not working, you'll likely find me spending time with my family, exploring new places, or hiking the scenic trails around the beautiful city of Los Angeles. I believe in living life with purpose — both in business and at home.",
  },
];


export const COMPANY_INFO = {
  name: 'Prime Sentinel Insurance Solutions',
  shortName: 'Prime Sentinel',
  phone: '818-600-0821',
  fax: '818-337-7125',
  email: 'info@primesentinelins.com',
  license: 'CA LIC No. 6013732',
  address: '15233 Ventura Blvd Suite 500',
  city: 'Sherman Oaks, CA 91403',
  googlePlaceId: 'ChIJyeXy4B6XwoARqGobBQ7-DzY',
  googleReviewUrl: 'https://search.google.com/local/writereview?placeid=ChIJyeXy4B6XwoARqGobBQ7-DzY',
  googleReviewsUrl: 'https://share.google/04F3rPpnKLoCoOuMn',
  rating: 5.0,
  reviewCount: 40,
  logo: '/images/logo.png',
  socials: {
    facebook: 'https://www.facebook.com/share/1E8Xfzf5co/?mibextid=wwXIfr',
    instagram: 'https://www.instagram.com/primesentinelinsurance',
    linkedin: 'https://www.linkedin.com/company/prime-sentinel-insurance-solutions/',
  },
};